import { useState } from "react";

import type { PathwayFacet } from "@app/types/dashboard-pathway";

/** A value as the rail draws it: `episodes` is null once the latest answer no longer lists it. */
export interface RailValue {
  value: string;
  episodes: number | null;
}

export type RailFacet = Omit<PathwayFacet, "values"> & { values: RailValue[] };

/**
 * `held` in its order, with what `served` adds placed after the served item it follows — or, first in
 * the answer, before the next one already held. An answer that is a superset lands in its own order.
 */
function merged<T>(held: T[], served: T[], keyOf: (item: T) => string): T[] {
  const out = [...held];
  const indexOf = (item: T) => out.findIndex((o) => keyOf(o) === keyOf(item));
  served.forEach((item, i) => {
    if (indexOf(item) >= 0) return;
    if (i > 0) {
      out.splice(indexOf(served[i - 1]) + 1, 0, item);
      return;
    }
    const next = served.find((s) => indexOf(s) >= 0);
    out.splice(next ? indexOf(next) : out.length, 0, item);
  });
  return out;
}

/** The new answer drawn on the rail's held layout: nothing it drops leaves its place, it goes uncounted. */
export function steadied(held: RailFacet[], served: PathwayFacet[]): RailFacet[] {
  const answer = new Map(served.map((facet) => [facet.key, facet]));
  return merged<RailFacet>(held, served, (facet) => facet.key).map((facet) => {
    const now = answer.get(facet.key);
    const counts = new Map((now?.values ?? []).map((v) => [v.value, v.episodes]));
    const values = merged<RailValue>(facet.values, now?.values ?? [], (v) => v.value).map((v) => ({
      value: v.value,
      episodes: counts.get(v.value) ?? null,
    }));
    return now ? { ...now, values } : { ...facet, values, more: 0, withheld: 0 };
  });
}

/**
 * The rail as the reader found it, while they work in it. A tick re-counts every other group, and the
 * groups and values the new answer drops pulled everything under them up beneath the cursor. Another
 * perimeter, or an answer dropped for another reader's, is another rail: its first answer is taken as is.
 */
export function useSteadyFacets(served: PathwayFacet[] | undefined, perimeter: string): RailFacet[] {
  const [held, setHeld] = useState<{
    served: PathwayFacet[] | undefined;
    perimeter: string;
    facets: RailFacet[];
    fresh: boolean;
  }>(() => ({ served, perimeter, facets: served ?? [], fresh: true }));
  if (served !== held.served || perimeter !== held.perimeter) {
    const fresh = perimeter !== held.perimeter || served === undefined;
    setHeld({
      served,
      perimeter,
      facets: !served ? [] : fresh || held.fresh ? served : steadied(held.facets, served),
      fresh,
    });
  }
  return held.facets;
}
